/**
 * Singleton Axios instance for all LOA API communication.
 *
 * @module apiClient
 *
 * @remarks
 * All HTTP requests in the application go through this instance.
 * Do not create additional Axios instances — extend this one via interceptors.
 *
 * ## Token lifecycle
 * The Authorization interceptor is installed / ejected **synchronously** at
 * three control-flow points:
 *   • `OAuthBridge` — on login + logout transitions surfaced by
 *     `react-oauth2-code-pkce`.
 *   • `useAuth.logout` — on manual logout.
 *   • The 401 response handler in `AppProvider` — on token expiry.
 *
 * It is intentionally **not** driven by a `useEffect`, because child-first
 * effect ordering can run a parent's eject after a child has already mounted
 * with the new token, racing the first authenticated request.
 *
 * ## 401 handling
 * `AppProvider` registers a handler via {@link setUnauthorizedHandler} on mount
 * that ejects the interceptor, flushes the TSQ cache, and fires `AUTH_LOGOUT`.
 *
 * @example
 * ```typescript
 * // In a TSQ query hook — no token plumbing needed
 * import { apiClient } from "@/lib/apiClient";
 *
 * const queryFn = () =>
 *   apiClient.get<{ results: Case[] }>("/cases/").then((r) => r.data.results);
 * ```
 */

import axios, { type AxiosInstance } from "axios";

import { getCurrentTenant } from "../auth/utils";

/**
 * Resolved API base URL.
 *
 * Falls back to the local backend (`api.loa.localhost`) when
 * `VITE_LOA_SERVICE_URL` is not provided — mirroring the same local default
 * used by {@link oauthConfig}. Without this, the dev server (`rushx dev`, Vite
 * mode `development`) leaves `baseURL` undefined, so API calls go to the SPA
 * origin and Vite's history fallback returns `index.html` instead of JSON,
 * breaking every page behind login.
 */
export const API_BASE_URL: string =
  (import.meta.env.VITE_LOA_SERVICE_URL as string) || "https://api.loa.localhost/api/v1";

/** Shared Axios instance — import this in all TSQ query/mutation functions. */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15_000,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// ─── Request interceptor: Bearer token injection ─────────────────────────────

let _token: string | null = null;

apiClient.interceptors.request.use((config) => {
  const tenant = getCurrentTenant();

  if (tenant) {
    config.headers["X-Client-Subdomain"] = tenant;
  }

  if (_token) {
    config.headers.Authorization = `Bearer ${_token}`;
  }

  return config;
});

/**
 * Sets (or clears) the Authorization header injected on every outgoing request.
 *
 * Must be called **synchronously** at the same moment as the auth state
 * transition (in OAuthBridge, useAuth.logout, or the 401 handler) — not from
 * a useEffect, which would race child-component effects on the same commit.
 * Passing `null` ejects the interceptor, leaving requests unauthenticated.
 *
 * @param token - JWT bearer token, or `null` to clear authentication.
 */
export function setAuthToken(token: string | null): void {
  _token = token;
}

/**
 * Reads the current Bearer token for callers that can't go through the
 * `apiClient` Axios instance — e.g. a raw `fetch` against a streaming (SSE)
 * endpoint, which needs the same Authorization header `apiClient`'s
 * interceptor injects automatically.
 */
export function getAuthToken(): string | null {
  return _token;
}

// ─── Response interceptor: 401 → AUTH_LOGOUT ────────────────────────────────

let _onUnauthorized: (() => void) | null = null;

/**
 * Registers the callback fired when the API responds with 401.
 *
 * Called by `AppProvider` on mount. Typically dispatches `AUTH_LOGOUT`
 * to the reducer so the user is redirected to the login page.
 *
 * @param fn - Callback to invoke on 401 responses.
 */
export function setUnauthorizedHandler(fn: () => void): void {
  _onUnauthorized = fn;
}

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      _onUnauthorized?.();
    }
    return Promise.reject(error);
  },
);
